import adj from 'src/internal/shared/adj';
import buildResponse from 'src/internal/shared/buildResponse';
import Headers from 'src/internal/shared/headers/headers';
import now from 'src/internal/shared/now';

import type {
  FetchHookOptions,
  FetchOptions,
  FetchResult,
  FetchState,
  FetchUrl,
  InterceptFetch,
} from 'src/internal/shared/fetch/types';

type FetchHookOptionsAndSignal<TData> = FetchHookOptions<TData> & {
  signal?: AbortSignal;
};

type FetchOptionsAndSignal = FetchOptions & {
  signal?: AbortSignal;
};

type BasicState<TData> = FetchState<TData>;

type ObservabilityConfig = {
  expectedErrors?: readonly number[];
  // mutedErrors are currently only passed by legacy APIs.
  // There is a plan to remove this property in the future.
  // Check doc: https://docs.google.com/document/d/1YH9PgKmQieGFJ5K75-oVQyyUM3AdIhEiDOdK50fpulo/edit?tab=t.0#heading=h.ic1l8w4gfir1
  mutedErrors?: readonly number[];
};

type FetchLogFn = (
  res: Response,
  duration: number,
  start: number,
  end: number,
  observabilityConfig: ObservabilityConfig,
) => void;

export default function fullFetch<TData>(
  url: FetchUrl,
  options: FetchHookOptionsAndSignal<TData>,
  fetchFunction: (
    url: FetchUrl,
    options: FetchOptionsAndSignal,
    interceptFetch?: InterceptFetch,
  ) => FetchResult,
  fetchFormat: 'text' | 'json',
  fetchLog: FetchLogFn,
  /**
   * TODO(guille): Remove interceptFetch added temporarily in go/pr/2561432
   */
  interceptFetch?: InterceptFetch,
): Promise<BasicState<TData> | null> {
  return new Promise((resolve) => {
    (async (): Promise<BasicState<TData> | null> => {
      const start = adj(now());

      const {
        expectedErrors,
        mutedErrors,
        timeout,
        retry,
        signal,
        ...fetchOptions
      } = options;

      let abortController: AbortController | null = null;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      // If the main signal controller is aborted, abort also the current request.
      signal?.addEventListener('abort', function abortListener() {
        resolve(null);

        abortController?.abort();
        abortController = null;

        signal?.removeEventListener('abort', abortListener);
      });

      let i = 0;

      /* eslint-disable no-await-in-loop, no-loop-func */
      do {
        abortController = new AbortController();

        const promiseData: Promise<BasicState<TData>> = (async (): Promise<
          BasicState<TData>
        > => {
          try {
            const res = await fetchFunction(
              url,
              {
                ...fetchOptions,
                signal: abortController.signal,
              },
              interceptFetch,
            );

            const data = await res[fetchFormat]();

            if (!res.ok) {
              const end = adj(now());
              fetchLog(res, adj(end - start), start, end, {
                expectedErrors,
                mutedErrors,
              });
            }

            return {
              called: true,
              loading: false,
              error: undefined,
              data,
              headers: new Headers(res.headers),
              redirected: res.redirected,
              status: res.status,
              statusText: res.statusText,
              type: res.type,
              url: res.url,
            };
          } catch (err) {
            const error = err instanceof Error ? err : new Error(`${err}`);

            const networkErrorRes = buildResponse('', {
              status: -1,
              statusText: error.message,
              url,
            });

            const end = adj(now());

            fetchLog(networkErrorRes, adj(end - start), start, end, {
              expectedErrors,
              mutedErrors,
            });

            return {
              called: true,
              loading: false,
              error,
              data: undefined,
              headers: undefined,
              redirected: undefined,
              status: undefined,
              statusText: undefined,
              type: undefined,
              url: undefined,
            };
          } finally {
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }
          }
        })();

        const promiseTimeout: Promise<BasicState<TData>> = (async (): Promise<
          BasicState<TData>
        > => {
          const err = new Error(`Fetch exceeded timeout of ${timeout} ms`);

          await new Promise((res) => {
            timeoutId = setTimeout(res, timeout || 2147483647);
          });

          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          abortController?.abort();

          const networkErrorRes = buildResponse('', {
            status: -1,
            statusText: err.message,
            url,
          });

          const end = adj(now());

          fetchLog(networkErrorRes, adj(end - start), start, end, {
            expectedErrors,
            mutedErrors,
          });

          return {
            called: true,
            loading: false,
            error: err,
            data: undefined,
            headers: undefined,
            redirected: undefined,
            status: undefined,
            statusText: undefined,
            type: undefined,
            url: undefined,
          };
        })();

        const result: BasicState<TData> = await Promise.race([
          promiseData,
          promiseTimeout,
        ]);

        // If there's been an abort error, return null to signal the abortion.
        if (result.error?.name === 'AbortError') {
          return null;
        }

        // If we do not want to retry, return the result.
        if (!(await retry?.(result, i))) {
          return result;
        }

        i++;
      } while (true);
      /* eslint-enable */
    })().then(resolve);
  });
}
